import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getCardKey, parseShinyItemKey, highestTier, SHINY_ITEM_PREFIX } from '@flip7/shared';
import type { Card, ShinyTier } from '@flip7/shared';

interface ShinyCardsContextValue {
  /** Get all owned tiers for a card */
  getTiers: (card: Card) => ShinyTier[];
  /** Get the highest owned tier for a card (for display) */
  getHighestTier: (card: Card) => ShinyTier | null;
  /** Map of cardKey -> owned tiers */
  shinyMap: Map<string, ShinyTier[]>;
  /** Total unique (card, tier) pairs owned */
  totalOwned: number;
}

const ShinyCardsContext = createContext<ShinyCardsContextValue>({
  getTiers: () => [],
  getHighestTier: () => null,
  shinyMap: new Map(),
  totalOwned: 0,
});

export function ShinyCardsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const shinyMap = useMemo(() => {
    if (!user) return new Map<string, ShinyTier[]>();
    const map = new Map<string, ShinyTier[]>();
    for (const itemKey of user.inventory) {
      if (!itemKey.startsWith(SHINY_ITEM_PREFIX)) continue;
      const parsed = parseShinyItemKey(itemKey);
      if (!parsed) continue;
      const existing = map.get(parsed.cardKey) ?? [];
      if (!existing.includes(parsed.tier)) existing.push(parsed.tier);
      map.set(parsed.cardKey, existing);
    }
    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.inventory]);

  const totalOwned = useMemo(() => {
    let count = 0;
    for (const tiers of shinyMap.values()) count += tiers.length;
    return count;
  }, [shinyMap]);

  const value = useMemo(() => ({
    getTiers: (card: Card) => shinyMap.get(getCardKey(card)) ?? [],
    getHighestTier: (card: Card) => highestTier(shinyMap.get(getCardKey(card)) ?? []),
    shinyMap,
    totalOwned,
  }), [shinyMap, totalOwned]);

  return (
    <ShinyCardsContext.Provider value={value}>
      {children}
    </ShinyCardsContext.Provider>
  );
}

export function useShinyCards() {
  return useContext(ShinyCardsContext);
}
